// @ts-strict-ignore
import {SankeyEdge} from './sankey-edge';
import {SankeyPart} from './sankey-part';
import {SerializedSankeyNode} from './serialized';
import {Alignment, SankeyAnchorType, SankeyNodeType, SankeyStepType} from './types';

export type SankeyNodeAttributes = {
  heights: number[];
  height: number;
  isInTopPath: boolean;
  isInRemoveRange: boolean;
  nodeType: string;
  stepType: SankeyStepType;
  anchorStepIndex: number;
  disableClick: boolean;
};

export class SankeyNode extends SankeyPart {
  public declare alignment: number;
  public declare edges: SankeyEdge[];
  public declare incomingEdges: SankeyEdge[];
  public declare index: number;
  public declare section: number;
  public declare conversionRateChange: number;
  public declare isCustomEvent: boolean;
  public declare isComputed: boolean;
  public declare letterLabel: string;
  public declare anchorStepIndex: number;
  public declare nextAnchorStepIndex: number;
  public declare numberOfStepsFromAnchor: number;
  public declare getTopNodes: () => SankeyNode[];

  constructor({
    serializedSankeyNode,
    letterLabel,
    anchorStepIndex,
    nextAnchorStepIndex,
    numberOfStepsFromAnchor,
    getTopNodesCallback,
    alignment,
    section,
  }: {
    serializedSankeyNode: SerializedSankeyNode;
    letterLabel: string;
    anchorStepIndex: number;
    nextAnchorStepIndex: number;
    numberOfStepsFromAnchor: number;
    getTopNodesCallback: () => SankeyNode[];
    alignment?: number;
    section?: number;
  }) {
    super(serializedSankeyNode);
    this.edges = (serializedSankeyNode.edges || []).map((edge) => new SankeyEdge(edge));
    this.incomingEdges = [];
    this.alignment = alignment;
    this.section = section;
    this.conversionRateChange = serializedSankeyNode.conversionRateChange;
    this.isCustomEvent = serializedSankeyNode.isCustomEvent;
    this.isComputed = serializedSankeyNode.isComputed;
    this.letterLabel = letterLabel;
    this.anchorStepIndex = anchorStepIndex;
    this.nextAnchorStepIndex = nextAnchorStepIndex;
    this.numberOfStepsFromAnchor = numberOfStepsFromAnchor;
    this.getTopNodes = getTopNodesCallback;
  }

  public addIncomingEdge(edge): void {
    this.incomingEdges.push(edge);
  }

  // edges are sorted by how high they appear in the flow, this property
  // is maintained by sorting each of the nodes in each step
  public sortEdges(): void {
    this.edges = this.edges.sort((a, b) => a.toNode.index - b.toNode.index);
  }

  public inAnchorStep(): boolean {
    return this.type === SankeyNodeType.Anchor && this.anchorType === SankeyAnchorType.Normal;
  }

  public isAnchorNode(): boolean {
    return this.type === SankeyNodeType.Anchor && this.anchorType !== SankeyAnchorType.Normal;
  }

  public isRelativeForwardAnchorNode(): boolean {
    return this.type === SankeyNodeType.Anchor && this.anchorType === SankeyAnchorType.RelativeForward;
  }

  public hasLift(): boolean {
    return !this.inAnchorStep() && !this.isAnchorNode() && !this.isDropOff && this.conversionRateChange !== 0;
  }

  public getAnchorIndex(): number {
    const letterIndex =
      this.alignment === Alignment.RIGHT && this.anchorType !== SankeyAnchorType.Normal
        ? this.section - 1
        : this.section;
    return Number.isInteger(letterIndex) ? letterIndex : null;
  }
}
